import { useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/context";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import Headline from "../Components/Elements/Headline";
import Card from "../Components/Cards/Card";
import { DateTime } from "luxon";
import { TbArrowLeft, TbEdit, TbTrash } from "react-icons/tb";
import MilestoneCheckCard from "../Components/Cards/CardMilestoneCheck";
import { updateMilestoneStatus, updateMilestone } from "../api";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../Components/Elements/Toast";
import NavSidebarEdit from "../Components/Sidebar/NavSidebarEdit";
import Popup from "../Components/Elements/Popup";
import InputText from "../Components/Inputs/InputText";
import InputDate from "../Components/Inputs/InputDate";
import { capitalizeString } from "../utils/stringUtils";
import ProgressCircle from "../Components/Elements/ProgressCircle";
import { useNotifications } from "../context/NotificationContext";

const GoalDetails = () => {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const { userData, setUserData } = useContext(AuthContext);
  const { loadNotifications } = useNotifications();
  const { copy } = useLanguage();
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [sidebarEditToggled, setSidebarEditToggled] = useState(false);
  const [milestoneToEdit, setMilestoneToEdit] = useState(null);
  const [milestoneEditPopupOpen, setMilestoneEditPopupOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: "",
    deadline: "",
    status: false,
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [milestoneDeletePopupOpen, setMilestoneDeletePopupOpen] =
    useState(false);
  const [imageError, setImageError] = useState(false);

  const goals = Array.isArray(userData?.goals) ? userData.goals : [];
  const milestones = Array.isArray(userData?.milestones)
    ? userData.milestones
    : [];
  // Find the specific goal
  const goal = goals.find((g) => g.id === parseInt(goalId));

  // Get milestones for this goal, sorted by deadline
  const goalMilestones = milestones
    .filter((m) => m.goal_id === parseInt(goalId))
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));

  // Calculate progress
  const totalMilestones = goalMilestones.length;
  const completedMilestones = goalMilestones.filter((m) => m.status).length;
  const progress =
    totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;

  // Calculate stats for pie chart and countdown
  const incompleteMilestones = totalMilestones - completedMilestones;

  const today = DateTime.now();
  const deadlineDate = DateTime.fromISO(goal.deadline);
  const daysLeft = Math.max(
    0,
    Math.ceil(deadlineDate.diff(today, "days").days)
  );

  const toggleMilestoneStatus = async (milestoneId) => {
    try {
      // Find the current milestone and calculate the new status
      const milestone = userData.milestones.find((m) => m.id === milestoneId);
      const updatedStatus = !milestone.status;

      // Update local state
      const newUserData = { ...userData };
      const milestoneIndex = newUserData.milestones.findIndex(
        (m) => m.id === milestoneId
      );
      newUserData.milestones[milestoneIndex].status = updatedStatus;
      newUserData.milestones[milestoneIndex].statusChangedAt =
        new Date().toISOString();
      setUserData(newUserData);

      // Call backend with the correct new status
      await updateMilestoneStatus(milestoneId, updatedStatus);

      setToast({
        message: updatedStatus
          ? copy.card_milestone.toast.marked_completed
          : copy.card_milestone.toast.marked_incomplete,
        type: "success",
      });
      loadNotifications();
    } catch (error) {
      console.error("Error updating milestone status:", error);
      setToast({
        message: copy.card_milestone.toast.status_failed,
        type: "error",
      });
    }
  };

  const handleEditGoal = () => setSidebarEditToggled(true);

  const handleEditClickMilestone = (milestoneId) => {
    const milestone = userData.milestones.find((m) => m.id === milestoneId);
    setMilestoneToEdit(milestoneId);
    setEditFormData({
      title: milestone.title,
      deadline: milestone.deadline?.split("T")[0] || "",
      status: milestone.status,
    });
    setMilestoneEditPopupOpen(true);
  };

  const handleEditMilestoneInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditMilestone = async () => {
    if (!milestoneToEdit) return;
    if (!editFormData.title) {
      setEditFormErrors({ title: copy.card_milestone.errors.title_required });
      return;
    }
    if (!editFormData.deadline) {
      setEditFormErrors({
        deadline: copy.card_milestone.errors.deadline_required,
      });
      return;
    }
    try {
      // Persist to backend
      const updatedMilestone = await updateMilestone(milestoneToEdit, {
        title: editFormData.title,
        deadline: editFormData.deadline,
      });

      // Update local state with the returned milestone (use .milestone property)
      const newUserData = { ...userData };
      const milestoneIndex = newUserData.milestones.findIndex(
        (m) => m.id === milestoneToEdit
      );
      if (milestoneIndex !== -1) {
        // console.log(updatedMilestone);
        newUserData.milestones[milestoneIndex] = {
          ...updatedMilestone,
        };
      }
      setUserData(newUserData);
      setMilestoneEditPopupOpen(false);
      setMilestoneToEdit(null);
      setEditFormErrors({});
      setToast({ message: copy.card_milestone.toast.updated, type: "success" });
    } catch (err) {
      console.error(err);
      setToast({
        message: copy.card_milestone.toast.update_failed,
        type: "error",
      });
    }
  };

  const handleDeleteClickMilestone = (milestoneId) => {
    setMilestoneToDelete(milestoneId);
    setMilestoneDeletePopupOpen(true);
  };

  const handleDeleteMilestone = async () => {
    if (!milestoneToDelete) return;
    try {
      const newUserData = { ...userData };
      newUserData.milestones = newUserData.milestones.filter(
        (m) => m.id !== milestoneToDelete
      );
      setUserData(newUserData);
      setMilestoneDeletePopupOpen(false);
      setMilestoneToDelete(null);
      setToast({ message: copy.card_milestone.toast.deleted, type: "success" });
    } catch (err) {
      console.error(err);
      setToast({
        message: copy.card_milestone.toast.delete_failed,
        type: "error",
      });
    }
  };

  const handleImageError = (e) => {
    console.error("Image failed to load:", e.target.src);
    setImageError(true);
    e.target.onerror = null;
    e.target.src = "https://via.placeholder.com/400x200?text=No+Image";
  };

  if (
    !userData ||
    !Array.isArray(userData.goals) ||
    !Array.isArray(userData.milestones)
  ) {
    return <div>{copy.goals.loading}</div>;
  }

  if (!goal) {
    return (
      <PageContentContainer>
        <div className="flex flex-col items-center justify-center h-full">
          <h1 className="text-2xl font-bold mb-4">{copy.goals.not_found}</h1>
          <button
            onClick={() => navigate("/goals")}
            className="flex items-center gap-2 text-accent hover:text-accent-dark"
          >
            <TbArrowLeft /> {copy.goals.back_to_goals}
          </button>
        </div>
      </PageContentContainer>
    );
  }

  return (
    <PageContentContainer>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}
      {sidebarEditToggled && (
        <NavSidebarEdit
          setSidebarEditToggled={setSidebarEditToggled}
          selectedGoal={goal}
        />
      )}
      <Headline
        headline={copy.goals.details_headline}
        description={copy.goals.details_description}
        buttonCTA={copy.goals.edit_goal}
        onClickLink={handleEditGoal}
        type="button"
      />
      <div className="grid grid-cols-1 md:grid-cols-[5fr_2fr] w-full gap-2 items-start h-full overflow-hidden">
        <div className="flex flex-col gap-2">
          <div className="grid md:grid-cols-[3fr_2fr] gap-2  max-h-[80vh] stretch">
            {goal.img && !imageError ? (
              <div className="mt-4">
                <img
                  src={goal.img}
                  alt={goal.title}
                  className="w-full h-65 object-cover rounded-lg card-base-border"
                  onError={handleImageError}
                  crossOrigin="anonymous"
                />
              </div>
            ) : (
              <div className="mt-4">
                <img
                  src="https://via.placeholder.com/400x200?text=No+Image"
                  alt={copy.goals.no_image}
                  className="w-full h-65 object-cover rounded-lg card-base-border"
                />
              </div>
            )}
            <Card
              title={goal.title}
              description={copy.goals.details_page.goal_description}
              className=""
            >
              <div className="flex flex-col gap-4 h-full justify-center">
                <div className="flex justify-between items-center">
                  <p className="font-medium">{copy.goals.details.category}:</p>
                  <p>{capitalizeString(goal.category)}</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="font-medium">{copy.goals.details.year}:</p>
                  <p>{goal.year}</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="font-medium">{copy.goals.details.deadline}:</p>
                  <p>
                    {DateTime.fromISO(goal.deadline).toLocaleString(
                      DateTime.DATE_MED
                    )}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="font-medium">{copy.goals.details.progress}:</p>
                  <p>{Math.round(progress)}%</p>
                </div>
              </div>
            </Card>
          </div>

          <Card
            title={copy.goals.details_page.milestones_title}
            description={copy.goals.details_page.milestones_description}
            className="max-h-77 md:min-h-77 md:pr-1 overflow-auto"
          >
            <div className="flex flex-col gap-2">
              {goalMilestones.map((milestone) => (
                <MilestoneCheckCard
                  key={milestone.id}
                  id={milestone.id}
                  title={milestone.title}
                  status={milestone.status}
                  toggleMilestoneStatus={toggleMilestoneStatus}
                  milestoneDropdown={() => [
                    {
                      text: copy.dropdown.edit,
                      onClick: () => handleEditClickMilestone(milestone.id),
                      icon: <TbEdit />,
                    },
                    {
                      text: copy.dropdown.delete,
                      onClick: () => handleDeleteClickMilestone(milestone.id),
                      icon: <TbTrash />,
                    },
                  ]}
                />
              ))}
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 md:grid-rows-[3fr_2fr] gap-2 h-full">
          <Card
            title={copy.goals.details_page.milestone_stats_title}
            description={copy.goals.details_page.milestone_stats_description}
            className="flex  items-center justify-center"
          >
            <div className="flex flex-row md:flex-col gap-4 justify-between h-full">
              <div className="flex-[3]">
                <ProgressCircle
                  value={
                    totalMilestones > 0
                      ? completedMilestones / totalMilestones
                      : 0
                  }
                />
              </div>

              <div className="w-full flex-[1] grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 text-base font-medium mt-4 gap-2">
                <div className="flex flex-col items-center card-base-border rounded-md p-2">
                  <p className="text-2xl">{completedMilestones}</p>
                  <p className="text-xs">
                    {copy.goals.details_page.stats.completed}
                  </p>
                </div>
                <div className="flex flex-col items-center card-base-border rounded-md p-2">
                  <p className="text-2xl">{totalMilestones}</p>
                  <p className="text-xs">
                    {copy.goals.details_page.stats.total}
                  </p>
                </div>
                <div className="flex flex-col items-center card-base-border rounded-md p-2">
                  <p className="text-2xl">{incompleteMilestones}</p>
                  <p className="text-xs">
                    {copy.goals.details_page.stats.remaining}
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <Card
            title={copy.goals.details_page.countdown_title}
            description={copy.goals.details_page.countdown_description}
            className="flex flex-col items-center justify-center"
            cardClassName="h-full"
          >
            <p className="text-3xl">
              {daysLeft} {copy.goals.details_page.countdown.days}
            </p>
            <p className="text-xs mt-1">
              {copy.goals.details_page.countdown.deadline_label}{" "}
              {deadlineDate.toLocaleString(DateTime.DATE_MED)}
            </p>
          </Card>
        </div>
      </div>

      {/* Edit Milestone Popup */}
      <Popup
        isOpen={milestoneEditPopupOpen}
        onClose={() => setMilestoneEditPopupOpen(false)}
        title={copy.goals.edit_milestone}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={() => setMilestoneEditPopupOpen(false)}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditMilestone}
      >
        <div className="flex flex-col gap-4">
          <InputText
            label={copy.card_milestone.form.label_title}
            placeholder={copy.card_milestone.form.placeholder_title}
            name="title"
            value={editFormData.title}
            onChange={handleEditMilestoneInputChange}
            error={editFormErrors.title}
            details={`${editFormData.title.length}/100 characters`}
          />
          <InputDate
            label={copy.card_milestone.form.label_deadline}
            name="deadline"
            value={editFormData.deadline}
            onChange={handleEditMilestoneInputChange}
            error={editFormErrors.deadline}
          />
        </div>
      </Popup>

      {/* Delete Milestone Popup */}
      <Popup
        isOpen={milestoneDeletePopupOpen}
        onClose={() => setMilestoneDeletePopupOpen(false)}
        title={copy.card_milestone.popup.delete_milestone_title}
        description={copy.card_milestone.popup.delete_milestone_description}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={() => setMilestoneDeletePopupOpen(false)}
        buttonPrimaryCTA={copy.dropdown.delete}
        onClickPrimaryLink={handleDeleteMilestone}
      />
    </PageContentContainer>
  );
};

export default GoalDetails;
